import React, { useEffect, useState } from 'react';
import { expressURL, authFetch } from '../utils/networkUtils';
import {
  decryptPhotoToObjectUrl,
  parseMediaEncryption
} from '../crypto/mediaPayload.ts';
import './ThreadPhoto.scss';

export function mediaSrc(url?: string) {
  if (!url) return '';
  if (/^https?:\/\//i.test(url) || url.startsWith('/')) return url;
  return '/user-media/' + encodeURI(url);
}

type ThreadPhotoProps = {
  message: {
    id?: string | number;
    mediaUrl?: string;
    mediaEncryption?: unknown;
  };
  threadKey?: CryptoKey | null;
  encryptionLocked?: boolean;
  onOpen?: (src: string) => void;
  className?: string;
  alt?: string;
};

function ThreadPhoto({
  message,
  threadKey = null,
  encryptionLocked = false,
  onOpen,
  className = '',
  alt = 'User submitted'
}: ThreadPhotoProps) {
  const envelope = parseMediaEncryption(message.mediaEncryption);
  const remoteSrc = mediaSrc(message.mediaUrl);
  const envelopeKey = envelope ? `${envelope.iv}:${envelope.mimeType}` : '';
  const [objectUrl, setObjectUrl] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(!!envelope);

  useEffect(() => {
    const nextEnvelope = parseMediaEncryption(message.mediaEncryption);
    if (!nextEnvelope) {
      setObjectUrl('');
      setError('');
      setLoading(false);
      return undefined;
    }
    if (encryptionLocked || !threadKey) {
      setObjectUrl('');
      setError('');
      setLoading(false);
      return undefined;
    }
    if (!remoteSrc) {
      setLoading(false);
      setError('Photo is missing.');
      return undefined;
    }

    let revoked = false;
    let createdUrl = '';
    setLoading(true);
    setError('');

    (async () => {
      try {
        const ciphertextUrl = message.id != null
          ? `${expressURL()}/api/messages/${encodeURIComponent(String(message.id))}/media`
          : remoteSrc;
        const response = await authFetch(ciphertextUrl, {
          headers: {
            'bypass-tunnel-reminder': 'any_value'
          }
        });
        if (!response.ok) {
          throw new Error('Could not download photo');
        }
        const ciphertext = new Uint8Array(await response.arrayBuffer());
        const url = await decryptPhotoToObjectUrl(threadKey, ciphertext, nextEnvelope);
        if (revoked) {
          URL.revokeObjectURL(url);
          return;
        }
        createdUrl = url;
        setObjectUrl(url);
        setLoading(false);
      } catch {
        if (!revoked) {
          setLoading(false);
          setError('Could not decrypt this photo.');
        }
      }
    })();

    return () => {
      revoked = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [encryptionLocked, envelopeKey, message.id, message.mediaEncryption, remoteSrc, threadKey]);

  if (!remoteSrc) return null;

  if (envelope && (encryptionLocked || !threadKey)) {
    return (
      <div className={`thread-photo-placeholder ${className}`.trim()} role="status">
        Unlock to view this photo
      </div>
    );
  }

  if (envelope && loading) {
    return (
      <div className={`thread-photo-placeholder ${className}`.trim()} role="status">
        Decrypting photo…
      </div>
    );
  }

  if (envelope && error) {
    return (
      <div className={`thread-photo-placeholder ${className}`.trim()} role="alert">
        {error}
      </div>
    );
  }

  const src = envelope ? objectUrl : remoteSrc;
  if (!src) return null;

  return (
    <button
      type="button"
      className={`chatscope-image-btn ${className}`.trim()}
      onClick={() => onOpen && onOpen(src)}
    >
      <img src={src} alt={alt} />
    </button>
  );
}

export default ThreadPhoto;
